import { z } from 'zod';

// Wire schema for the itinerary consistency checker. Mirrors the AI module's
// `ConsistencyFlag` / persisted `itinerary_consistency_check` row, serialised
// for the tRPC boundary. The flag-generation logic lives in
// `@travel-planner/shared/ai` (`checkItineraryConsistency`); this file only
// describes the shapes the router exchanges with the web client.

export const consistencyFlagTypeSchema = z.enum([
  'description_mismatch',
  'overlapping_items',
  'scheduling_gap',
  'missing_transport',
]);
export type ConsistencyFlagTypeWire = z.infer<typeof consistencyFlagTypeSchema>;

export const consistencyFlagSeveritySchema = z.enum([
  'info',
  'warning',
  'error',
]);
export type ConsistencyFlagSeverityWire = z.infer<
  typeof consistencyFlagSeveritySchema
>;

export const consistencyFlagSchema = z.object({
  id: z.string(),
  type: consistencyFlagTypeSchema,
  severity: consistencyFlagSeveritySchema,
  title: z.string(),
  description: z.string(),
  itemId: z.string().nullable(),
  itemTitle: z.string().nullable(),
  suggestedFix: z.string().nullable(),
  relatedItemIds: z.array(z.string()).nullable(),
});
export type ConsistencyFlagWire = z.infer<typeof consistencyFlagSchema>;

export const consistencyCheckResultSchema = z.object({
  id: z.string(),
  itineraryId: z.string(),
  flagCount: z.number().int(),
  flags: z.array(consistencyFlagSchema),
  lastRunAt: z.string(),
  runByAccountId: z.string().nullable(),
});
export type ConsistencyCheckResult = z.infer<
  typeof consistencyCheckResultSchema
>;

// `get` returns the persisted result, or null when the check has never run.
export const consistencyCheckResultOrNullSchema =
  consistencyCheckResultSchema.nullable();

export const runConsistencyCheckInput = z.object({
  itineraryId: z.uuid(),
});
export type RunConsistencyCheckInput = z.infer<typeof runConsistencyCheckInput>;

export const getConsistencyCheckInput = z.object({
  itineraryId: z.uuid(),
});
export type GetConsistencyCheckInput = z.infer<typeof getConsistencyCheckInput>;
